/**
 * FocalCompanyFinancials — shared component that surfaces the focal company's
 * financial dashboard (P&L + Balance Sheet + Revenue→EV Sankey on the full
 * "finance" section; KPI cards + summary table on the summary "overview"
 * section) wherever a Case workflow needs to show it.
 *
 * Both modes delegate to the existing <FinancialDashboardTab /> in
 * src/components/financial — so the data fetching, country routing, year
 * picker, and chart components stay in one place. This file is intentionally
 * a thin wrapper that fixes the section choice and surfaces a graceful
 * "no company linked" empty state for use inside Case Library tabs.
 */
import { AlertCircle } from 'lucide-react';
import FinancialDashboardTab from '../financial/FinancialDashboardTab';

interface FocalCompanyFinancialsProps {
  /** Company name as stored in MongoDB. Required for the financials lookup. */
  companyName: string | null | undefined;
  /** Optional country hint ('US' | 'INDIA' | 'UK'). When omitted the backend
   *  picks the default route — same behaviour as CompanyDetail when uiConfig
   *  hasn't resolved yet. */
  country?: string;
  /**
   * `summary` → "overview" section (KPI cards + summary table). Compact view
   * suitable for inline mounts inside CaseDetail or wizard steps.
   * `full`    → "finance" section (P&L + Balance Sheet + Revenue→EV Sankey).
   * Default `full` matches the use inside CaseDetail's Financials tab.
   */
  mode?: 'summary' | 'full';
  /**
   * Explicit FinancialDashboardTab section — takes precedence over `mode`
   * when provided. Use this to mount Revenue & Capital ("revenue") or
   * Sensitivity Analysis ("sensitivity") sections directly.
   */
  section?: 'overview' | 'finance' | 'ev' | 'revenue' | 'sensitivity';
}

export default function FocalCompanyFinancials({
  companyName,
  country,
  mode = 'full',
  section,
}: FocalCompanyFinancialsProps) {
  if (!companyName) {
    return (
      <div className="flex items-center gap-2 p-4 bg-neutral-50 border border-neutral-200 rounded-lg text-neutral-600">
        <AlertCircle className="h-4 w-4 shrink-0 text-neutral-400" />
        <p className="text-sm">
          No company linked to this case yet. Link a Company Research record to see financials.
        </p>
      </div>
    );
  }

  const resolvedSection = section ?? (mode === 'summary' ? 'overview' : 'finance');

  return (
    <FinancialDashboardTab
      companyName={companyName}
      section={resolvedSection}
      country={country}
    />
  );
}
